import React, { useMemo, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSelector, shallowEqual } from 'react-redux';
import QuestionDistributionChart from './QuestionDistributionChart';
import MiniQuestionChart from './MiniQuestionChart';

const COLOR_COMPLETE = '#1D9A7D';

function isValidResponse(value) {
    if (value === null || value === undefined) return false;
    if (value === 'N/A' || value === '') return false;
    if (isNaN(Number(value))) return false;
    return true;
}

const QuestionContent = ({ selected, core_data, data }) => {
    const anchor = useSelector((state) => state.audit?.anchor, shallowEqual);
    const isFactor = selected?.type === 'factor' || selected?.type === 'outcome_factor' || selected?.questionCount !== undefined;
    const isOutcomeQuestion = selected?.type === 'outcome_question' || (selected?.isOutcome && selected?.q !== undefined && selected?.s !== undefined);

    const [isAnchorTransitioning, setIsAnchorTransitioning] = useState(false);
    useEffect(() => {
        setIsAnchorTransitioning(true);
        const t = setTimeout(() => setIsAnchorTransitioning(false), 180);
        return () => clearTimeout(t);
    }, [anchor, selected]);

    const totals = useMemo(() => {
        if (!selected || !data) return null;
        if (isFactor) {
            let completed = 0;
            let total = 0;
            if (selected.isOutcome || selected.type === 'outcome_factor' || selected.outcomeCategory !== undefined) {
                const category = core_data?.outcomeQuestions?.questions?.[selected.outcomeCategory];
                const questionCount = category?.questions?.length || 0;
                for (const r of data) {
                    total += questionCount;
                    const matches = (r.employee_outcomes?.responses || []).filter((resp) =>
                        (category?.questions || []).some((oq) => String(oq.q) === String(resp.q) && String(oq.s) === String(resp.s))
                    );
                    completed += matches.filter((m) => isValidResponse(m.response)).length;
                }
            } else {
                const factor = core_data?.questions?.dimensions?.[selected.dimension]?.factors?.[selected.factor];
                const questionCount = factor?.questions?.length || 0;
                for (const r of data) {
                    const matches = (r.questions || []).filter(
                        (q) => q.id === selected.dimension && q.factor === selected.factor
                    );
                    total += questionCount;
                    completed += matches.filter((m) => isValidResponse(m.response)).length;
                }
            }
            const percent = total > 0 ? (completed / total) * 100 : 0;
            return { completed, total, percent };
        } else {
            let completed = 0;
            let total = 0;
            if (isOutcomeQuestion) {
                for (const r of data) {
                    total += 1; // one potential per person for a specific outcome question
                    const match = (r.employee_outcomes?.responses || []).find(
                        (resp) => String(resp.q) === String(selected.q) && String(resp.s) === String(selected.s)
                    );
                    if (match && isValidResponse(match.response)) completed += 1;
                }
            } else {
                for (const r of data) {
                    total += 1; // one potential per person for specific question
                    const match = (r.questions || []).find(
                        (q) => q.id === selected.dimension && q.factor === selected.factor && q.q === selected.q
                    );
                    if (match && isValidResponse(match.response)) completed += 1;
                }
            }
            const percent = total > 0 ? (completed / total) * 100 : 0;
            return { completed, total, percent };
        }
    }, [selected, data, isFactor]);

    const distribution = useMemo(() => {
        if (!totals) return null;
        return [
            { name: 'Complete', data: [totals.percent], color: COLOR_COMPLETE, count: totals.completed },
        ];
    }, [totals]);

    const anchorBreakdowns = useMemo(() => {
        if (!core_data?.categories?.[0]?.categories || !anchor || !selected || !data) return [];
        const categories = core_data.categories[0].categories;
        const cur = categories.find((c) => c.id === anchor);
        if (!cur) return [];

        return cur.options.map((opt) => {
            let completed = 0;
            let total = 0;
            for (const r of data) {
                const cat = (r.categories || []).find((c) => c.id === anchor);
                if (!cat || cat.response !== opt.id) continue;

                if (isFactor) {
                    if (selected.isOutcome || selected.type === 'outcome_factor' || selected.outcomeCategory !== undefined) {
                        const category = core_data?.outcomeQuestions?.questions?.[selected.outcomeCategory];
                        const questionCount = category?.questions?.length || 0;
                        total += questionCount;
                        const matches = (r.employee_outcomes?.responses || []).filter((resp) =>
                            (category?.questions || []).some((oq) => String(oq.q) === String(resp.q) && String(oq.s) === String(resp.s))
                        );
                        completed += matches.filter((m) => isValidResponse(m.response)).length;
                    } else {
                        const factor = core_data?.questions?.dimensions?.[selected.dimension]?.factors?.[selected.factor];
                        const questionCount = factor?.questions?.length || 0;
                        const matches = (r.questions || []).filter(
                            (q) => q.id === selected.dimension && q.factor === selected.factor
                        );
                        total += questionCount;
                        completed += matches.filter((m) => isValidResponse(m.response)).length;
                    }
                } else if (isOutcomeQuestion) {
                    total += 1;
                    const match = (r.employee_outcomes?.responses || []).find(
                        (resp) => String(resp.q) === String(selected.q) && String(resp.s) === String(selected.s)
                    );
                    if (match && isValidResponse(match.response)) completed += 1;
                } else {
                    total += 1;
                    const match = (r.questions || []).find(
                        (q) => q.id === selected.dimension && q.factor === selected.factor && q.q === selected.q
                    );
                    if (match && isValidResponse(match.response)) completed += 1;
                }
            }
            const percent = total > 0 ? (completed / total) * 100 : 0;
            const chart = [
                { name: 'Complete', data: [percent], color: COLOR_COMPLETE, count: completed },
            ];
            return { title: opt.name, distribution: chart };
        }).filter(Boolean);
    }, [anchor, core_data, data, isFactor, selected]);

    if (!selected || !totals) return null;

    return (
        <Wrap>
            <Header>
                <Title>{selected.title}</Title>
                <Meta>
                    <strong>{Math.round(totals.percent)}%</strong> compliance • {totals.completed}/{totals.total} answered
                </Meta>
            </Header>

            <MainChart transitioning={isAnchorTransitioning}>
                <QuestionDistributionChart data={distribution} />
            </MainChart>

            {anchorBreakdowns && anchorBreakdowns.length > 0 && (
                <Breakdowns>
                    {anchorBreakdowns.map((b, i) => (
                        <MiniQuestionChart key={i} title={b.title} data={b.distribution} />
                    ))}
                </Breakdowns>
            )}
        </Wrap>
    );
};

export default QuestionContent;

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Header = styled.div`
    display: flex;
    align-items: baseline;
    gap: 12px;
`;

// Back button removed; outer header provides navigation

const Title = styled.h3`
    margin: 0;
    font-size: 18px;
    color: #111827;
`;

const Meta = styled.div`
    color: #6b7280;
    font-size: 13px;
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const MainChart = styled.div`
    width: 100%;
    position: relative;
    opacity: ${p => (p.transitioning ? 0.35 : 1)};
    transition: opacity 160ms ease;
    &::after {
        content: '';
        display: ${p => (p.transitioning ? 'block' : 'none')};
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(240,244,255,0.7) 50%, rgba(255,255,255,0) 100%);
        animation: ${shimmer} 600ms ease-out infinite;
    }
`;

const Breakdowns = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

